import { FC, useCallback, useMemo, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import { Box, Stack } from '@mui/material';
import DataTable from '@/components/data-table';
import PageHeader from '@/components/page/header';
import Chip from '@/components/chip';
import {
  DtColumn,
  DtFilter,
  DtFilterField,
  DtFilterValue,
  DtPage,
  DtSort,
} from '@/components/data-table/helpers';
import { capitalize, cloneDeep } from 'lodash';
import ActionChip from '@/components/action-chip';
import NiceDate from '@/components/nice-date';
import Text from '@/components/text';
import { searchUsers } from '@/lib/services/user.service';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import NoneChip from '@/components/chip/none-chip';
import useAppData from '@/hooks/use-app-data.hook';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { getUserActivityCount, getUserEvents } from '@/lib/services/user-event.service';
import { UserEventAction, Violation } from '@/lib/models/user-event';
import { ReferrerRelation, violationFilterValue } from '@/lib/models/user-event/helpers';
import { getUserEventActionProps } from '@/components/action-chip/helpers';
import { AppDataQueryKey, QueryKey } from '@/lib/query-client';
import { predefinedSavedFilters } from './saved-filters';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import RiskChip from '@/components/chip/risk-chip';
import { getDaysAgoDate } from '@/lib/helpers';
import PageBlurb from '@/components/page/blurb';
import UserLink from '@/components/user-link';
import ViolationChip from '@/components/chip/violation-chip';
import useDrawer from '@/hooks/use-drawer.hook';
import useDataTableCursor from '@/hooks/use-datatable-cursor';
import ServiceLink from '@/components/service-link';
import { getUserEventPublicService } from '@/lib/services/public-service.service';
import { getDomain } from '@/lib/url-helpers';

const columns: DtColumn[] = [
  { name: 'user', label: 'User', flex: 1.2, sortable: false },
  { name: 'intent', label: 'Intent', sortable: false },
  { name: 'service', label: 'Service', flex: 1.3 },
  { name: 'riskScore', label: 'Risk Level', flex: 0.7, sortable: true },
  { name: 'violations', label: 'Violations', flex: 0.8, sortable: false },
  { name: 'action', label: 'Result', flex: 0.8 },
  { name: 'startTime', label: 'Date', flex: 0.8 },
  { name: 'actions', label: '', flex: 0.2, sortable: false },
];

const filter: DtFilter = {
  keyword: '',
  fields: [
    {
      columnName: 'startTime',
      label: 'Date',
      control: 'date-range',
      minDate: () => getDaysAgoDate(30),
      maxDate: () => new Date(),
    },
    {
      columnName: 'action',
      label: 'Result',
      control: 'select-multi',
      highlander: true,
      options: [
        UserEventAction.ALLOW,
        UserEventAction.BLOCK,
        UserEventAction.PORTAL,
        UserEventAction.DOWNTIME,
        UserEventAction.ERROR,
        UserEventAction.INTERCEPTAUTH,
        UserEventAction.REDACT,
        UserEventAction.PASSTHROUGH,
      ].map((action) => ({
        label: capitalize(getUserEventActionProps(action).label),
        value: String(action),
      })),
    },
    {
      columnName: 'violation',
      label: 'Violation',
      additional: true,
      control: 'select-multi',
      highlander: true,
      options: [
        Violation.PII,
        Violation.INTENT,
        Violation.ENTITY,
        Violation.ACCESS,
        Violation.CONFIDENTIAL_DATA,
        Violation.PROMPT_INJECTION,
        Violation.TOXICITY,
        Violation.CODE,
        Violation.BIAS,
        Violation.RISK,
        Violation.TOOL,
        Violation.TRAINING,
        Violation.LICENSE,
        Violation.FILE_UPLOAD,
      ].map((violation) => ({ label: violation, value: violationFilterValue(violation) })),
    },
    {
      columnName: 'user',
      label: 'User',
      additional: true,
      searchLabel: 'Search by name or email',
      control: 'select-search',
      onSearch: async (value: string) => {
        const users = value ? await searchUsers(value) : [];
        return users.map(({ name, email, id }) => ({
          labelSimple: name || email,
          label: <Text size="small">{name && email ? `${name} <${email}>` : name || email}</Text>,
          value: id,
        }));
      },
    },
    {
      columnName: 'riskLevel',
      label: 'Risk Level',
      control: 'select-multi',
      highlander: true,
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
        { label: 'Critical', value: 'critical' },
      ],
    },
    {
      columnName: 'referrerRelation',
      label: 'Referrer',
      additional: true,
      control: 'select',
      emptyMessage: 'Any Referrer',
      options: [
        {
          value: ReferrerRelation.ALIGNED,
          label: 'Aligned',
          description: 'Same host or service as the request, or no referrer at all.',
        },
        {
          value: ReferrerRelation.MISMATCHED,
          label: 'Misaligned',
          description: 'A different service, an external site, or could not be resolved.',
        },
      ],
    },
    {
      columnName: 'referrers',
      label: 'Referrer Hosts',
      additional: true,
      control: 'text-multi',
      searchLabel:
        'Add one or more substrings to match against the referrer URL. Matching is case-insensitive, and an event matches if its referrer contains any one of them.',
    },
  ],
  values: [],
};

const addLikeActionFilterValue = (
  filterValues: DtFilterValue[],
  sourceAction: UserEventAction,
  likeActionValue: UserEventAction
): DtFilterValue[] => {
  const sourceActionVal = String(sourceAction);
  const likeActionVal = String(likeActionValue);

  for (const filterValue of filterValues) {
    const { columnName, value } = filterValue;
    if (columnName === 'action') {
      const hasMultiple = Array.isArray(value);
      const hasSourceAction =
        (hasMultiple && value.includes(sourceActionVal)) || value === sourceActionVal;
      const needsLikeAction = hasSourceAction && (!hasMultiple || !value.includes(likeActionVal));
      if (hasSourceAction && needsLikeAction) {
        if (!hasMultiple) {
          filterValue.value = [filterValue.value];
        }
        (filterValue.value as string[]).push(likeActionVal);
      }
    }
  }

  return filterValues;
};

const UserEventsPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const { getNextCursor, updateLastCursor, getCursorTotal } = useDataTableCursor();
  const [selectedRowId, setSelectedRowId] = useState('');
  const [polling, setPolling] = useState(true);
  const { loading: featuresLoading, hasDemoUserEventFilter } = useFeature();

  const handleExport = async (
    _page: number,
    _pageSize: number,
    _sort?: DtSort,
    filter?: DtFilter
  ) => {
    setPolling(false);
    openDrawer('user-event-export', {
      onClose: () => {
        setPolling(true);
      },
      persistToUrl: false,
      filter,
    });
    return Promise.resolve();
  };

  /*
   * As a convenience feature, if a user event policy decision is either "block" or "inline block", we
   * simply show "block" in the admin. So when filtering on policy decision, we need to ensure that
   * both "block" and "inline block" events are included in filtered results. And the same goes
   * for "allow" and "reroute". For the user, the displayed policy decision is just "allow".
   */
  const handleChangeFilter = (filterValues: DtFilterValue[]) => {
    addLikeActionFilterValue(filterValues, UserEventAction.BLOCK, UserEventAction.INLINEBLOCK);
    addLikeActionFilterValue(filterValues, UserEventAction.ALLOW, UserEventAction.REROUTE);
    addLikeActionFilterValue(filterValues, UserEventAction.ERROR, UserEventAction.HTTPERROR);
    return filterValues;
  };

  const handleView = useCallback(
    (id: string) => {
      setSelectedRowId(id);
      openDrawer('user-event', {
        id,
        onClose: () => {
          setSelectedRowId('');
        },
      });
    },
    [openDrawer]
  );

  const psQuery = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data: publicServices = [] } = psQuery;
  const { showLoader: psLoading } = useQueryHelper(psQuery);

  const compFilter = useMemo(() => {
    const publicServiceOptions = publicServices.map(({ id, name }) => ({
      labelSimple: name,
      label: (
        <Box maxWidth={280}>
          <Text size="small" dotdot>
            {name}
          </Text>
        </Box>
      ),
      value: id,
    }));

    const publicServiceFilter: DtFilterField = {
      columnName: 'serviceId',
      label: 'Service',
      control: 'select-search',
      highlander: true,
      options: publicServiceOptions,
      onSearch: async (value: string) => {
        const safeKeyword = String(value).trim().toLowerCase();
        return Promise.resolve(
          publicServiceOptions.filter(({ labelSimple }) =>
            labelSimple.toLowerCase().includes(safeKeyword)
          )
        );
      },
    };

    const compFilter = cloneDeep(filter);

    // add the default filter to exclude errors, when demoing
    return {
      keyword: '',
      fields: [...compFilter.fields, publicServiceFilter],
      values: hasDemoUserEventFilter
        ? [
            {
              columnName: 'action',
              value: [
                UserEventAction.ALLOW,
                UserEventAction.REROUTE,
                UserEventAction.BLOCK,
                UserEventAction.INLINEBLOCK,
                UserEventAction.PORTAL,
                UserEventAction.DOWNTIME,
                UserEventAction.INTERCEPTAUTH,
                UserEventAction.REDACT,
                UserEventAction.PASSTHROUGH,
              ].map((value) => String(value)),
            },
          ]
        : [],
    };
  }, [hasDemoUserEventFilter, publicServices]);

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter, polling?: boolean) => {
      const { page, pageSize, sort } = pageParams;
      const nextCursor = getNextCursor(pageParams, filter);

      const result = await getUserEvents(
        nextCursor,
        pageSize,
        !sort || polling ? { columnName: 'startTime', direction: 'desc' } : sort,
        filter
      );

      updateLastCursor(result);

      const cursorTotal = getCursorTotal(pageParams);

      return {
        cursor: result.cursor,
        page,
        pageSize,
        sort,
        filter,
        rows: result.rows.map((event) => {
          const {
            id,
            intentLabels,
            userClaim,
            startTime,
            serviceName,
            isF5WAIS,
            policyDecision,
            violations,
            requestUrl,
            riskLevel,
            firewallDecision,
            fileUpload,
          } = event;

          const publicService = getUserEventPublicService(event, publicServices);

          let ServiceEl = (
            <Text dotdot size="small">
              {serviceName}
            </Text>
          );

          if (publicService) {
            const subtext = publicService.isDiscovered
              ? getDomain(requestUrl)
              : isF5WAIS
                ? firewallDecision?.target?.name
                : '';

            ServiceEl = <ServiceLink service={publicService} subtext={subtext} iconSize={24} />;
          }

          const numberOfViolations = violations?.length || 0;
          const additionalViolations = numberOfViolations - 1;

          return {
            id,
            user: <UserLink user={userClaim} showAvatar={false} action="none" />,
            intent: intentLabels.domains ? (
              <Text size="small">{intentLabels.domains}</Text>
            ) : (
              <NoneChip notAvailable />
            ),
            service: ServiceEl,
            action: <ActionChip action={policyDecision} fileUpload={fileUpload} />,
            violations: numberOfViolations ? (
              <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                <ViolationChip violation={violations[0]} />
                {!!additionalViolations && (
                  <Chip size="small" color="grey" label={`+${additionalViolations} more`} />
                )}
              </Stack>
            ) : (
              <NoneChip />
            ),
            startTime: (
              <Stack>
                <NiceDate size="x-small" val={startTime} withTime />
              </Stack>
            ),
            riskScore: riskLevel ? <RiskChip value={riskLevel} /> : <NoneChip notAvailable />,
            actions: <OpenDrawerButton onClick={() => handleView(id)} />,
          };
        }),
        total: cursorTotal,
      };
    },
    [getNextCursor, updateLastCursor, getCursorTotal, handleView, publicServices]
  );

  if (featuresLoading || psLoading) {
    return null;
  }

  return (
    <Page title="User Activity">
      <PageHeader>
        <PageBlurb>
          View up to 30 days of user activity, including related models, policy decisions, and
          conversations.
        </PageBlurb>
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.UserEventsDataset}
          searchPlaceholder="Search messages"
          columns={columns}
          onLoad={loadData}
          onLoadCount={getUserActivityCount}
          onChangeFilter={handleChangeFilter}
          rowHeight={70}
          filter={compFilter}
          savedFilters={predefinedSavedFilters}
          search
          poll={polling ? 15000 : 0}
          selectedRowId={selectedRowId}
          onExport={handleExport}
          mode="cursor"
          countSuffix="User Events Found"
        />
      </DomSize>
    </Page>
  );
};

export default UserEventsPage;
